import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import printPdf from 'utils/printPdf';
import { uniq } from 'lodash';
import { Helmet } from 'react-helmet';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';

import SchedulePreviewEditForm from 'components/schedule/SchedulePreviewEditForm';

import getPatientAddress from 'utils/print/getPatientAddress';
import getPatientName from 'utils/print/getPatientName';
import getPatientCity from 'utils/print/getPatientCity';
import getPatientZip from 'utils/print/getPatientZip';
import getPatientFullPhone from 'utils/print/getPatientFullPhone';
import getPatientDeliveryNotes from 'utils/print/getPatientDeliveryNotes';

import { appTitle, titleSeparator } from 'config/helmetSettings';
import dateFormat from 'config/dateFormat';
import timeFormat from 'config/timeFormat';
import timeDateFormat from 'config/timeDateFormat';

import scheduleToPDF from 'utils/scheduleToPDF';

import { fetchPatients } from 'redux/modules/patients';
import { fetchPatientGlobalNotes } from 'redux/modules/patientsGlobalNotes';
import { fetchNoteCategories } from 'redux/modules/noteCategories';
import { createOrderNote } from 'redux/modules/ordersNotes';

class DeliverySchedulePrint extends Component {
  componentDidMount() {
    const { match, location, variant, history, actions } = this.props;
    if (!location.state || !location.state.items) {
      history.replace(`/workflow/delivery/schedule/${variant}`);
    } else {
      const schedule = location.state.items.filter(item =>
        (item[0]['Order Line'][0].Driver.ID === Number(match.params.id)
          || (match.params.id === 'unassigned' && !item[0]['Order Line'][0].Driver.ID))
        && moment(item[0]['Order Line'][0]['Date of Delivery'], timeDateFormat).format('MM-DD-YYYY') === match.params.dateOfDelivery)[0];
      if (schedule) {
        const patientRecordNumbers = uniq(schedule.map(item => item.Patient['Record Number']));
        actions.fetchPatients({
          filters: [{
            name: 'Record Number',
            operation: 'in',
            val: patientRecordNumbers,
          }],
          sort_by: [{
            name: 'Record Number',
            sort: 'asc',
          }],
          triggerLoading: true,
          resolve: () => {
            actions.fetchPatientGlobalNotes({
              values: {
                'Record Number': patientRecordNumbers,
                // 'Is Delivery': true,
              },
              triggerLoading: true,
            });
          },
        });
        actions.fetchNoteCategories({
          filters: [{
            name: 'Is Delivery',
            operation: 'eq',
            val: true,
          }],
        });
      }
    }
  }

  render() {
    const { match, variant, patients, loading, record, history, noteCategories, account } = this.props;
    const { state } = this.props.location;
    if (!state || loading > 0) {
      return (
        <Layout
          variant="print"
          titleText="Schedule"
        >
          <Progress />
        </Layout>
      );
    }
    const navigationSections = state.items.map((schedule) => {
      const driver = schedule[0]['Order Line'][0].Driver;
      const dayOfDelivery = moment(schedule[0]['Order Line'][0]['Date of Delivery'], timeDateFormat);
      if (!driver.ID) {
        return {
          title: `Driver not assigned ${dayOfDelivery.format(dateFormat)}`,
          href: `/workflow/delivery/schedule/${variant}/print/unassigned/${dayOfDelivery.format('MM-DD-YYYY')}`,
          state: {
            items: state.items,
          },
        };
      }
      return {
        title: `${driver['First Name']} ${driver['Last Name']} ${dayOfDelivery.format(dateFormat)}`,
        href: `/workflow/delivery/schedule/${variant}/print/${driver.ID}/${dayOfDelivery.format('MM-DD-YYYY')}`,
        state: {
          items: state.items,
        },
      };
    });
    let schedule = state.items.filter(item =>
      (item[0]['Order Line'][0].Driver.ID === Number(match.params.id)
        || (match.params.id === 'unassigned' && !item[0]['Order Line'][0].Driver.ID))
      && moment(item[0]['Order Line'][0]['Date of Delivery'], timeDateFormat).format('MM-DD-YYYY') === match.params.dateOfDelivery)[0];
    if (!schedule) {
      return '';
    }
    schedule = schedule.map((item) => {
      const patient = patients.filter(patientsItem => patientsItem['Record Number'] === item.Patient['Record Number'])[0];
      if (patient) {
        return {
          ...item,
          Patient: {
            ...item.Patient,
            ...patient,
          },
        };
      }
      return item;
    });
    const scheduleDriver = schedule[0]['Order Line'][0].Driver;
    const momentScheduleDate = moment(schedule[0]['Order Line'][0]['Date of Delivery'], timeDateFormat);
    const scheduleDriverName = scheduleDriver.ID
      ? `${scheduleDriver['First Name']} ${scheduleDriver['Last Name']}`
      : 'Driver not assigned';
    const initialValues = schedule.reduce((accumulator, currentValue) => ({
      ...accumulator,
      ...{ [`schedule-${currentValue.ID}`]: {
        orderID: currentValue['Order Line'][0]['Order ID'],
        timeOfDelivery: moment(currentValue['Order Line'][0]['Date of Delivery'], timeDateFormat)
          .format(timeFormat),
        patientName: `${getPatientName({ patient: currentValue.Patient })}\n`,
        address: getPatientAddress({ patient: currentValue.Patient })
          ? `${getPatientAddress({ patient: currentValue.Patient })}\n` : '',
        city: getPatientCity({ patient: currentValue.Patient })
          ? `${getPatientCity({ patient: currentValue.Patient })}\n` : '',
        zip: getPatientZip({ patient: currentValue.Patient }) || '',
        phone: getPatientFullPhone({ patient: currentValue.Patient }) || '',
        products: currentValue['Order Line']
          .map(orderLine => (
            orderLine.Product['Product Name']
              ? `${orderLine.Product['Product Name']} ${orderLine.Product.Model || ''} x${orderLine.Units};\n`
              : `${orderLine.Description} x${orderLine.Units};\n`))
          .join(''),
        comments: `${getPatientDeliveryNotes({ patient: currentValue.Patient })}\n` || '',
      } },
    }), {
      Name: `${scheduleDriverName}, ${momentScheduleDate.format(dateFormat)}`,
      'Save Comments': true,
    });
    const lastRecordBeforeDocumentPreviewFill = record.slice().reverse()
      .find(item => item.pathname.indexOf('/print/') === -1);
    const backHref = lastRecordBeforeDocumentPreviewFill
      ? `${lastRecordBeforeDocumentPreviewFill.pathname}${lastRecordBeforeDocumentPreviewFill.search}`
      : `/workflow/delivery/schedule/${variant}`;
    return (
      <Layout
        variant="print"
        navigationSections={[{ items: navigationSections }]}
        titleText="Schedule"
      >
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Schedule Print
          </title>
        </Helmet>
        <SchedulePreviewEditForm
          variant={variant}
          form={`${scheduleDriver.ID}-${momentScheduleDate.format('MM-DD-YYYY')}-schedulePreviewEditForm`}
          schedule={schedule}
          initialValues={initialValues}
          onSubmit={(values, dispatch, props) => {
            if (values['Save Comments']) {
              const scheduleValues = Object.assign({}, props.values);
              delete scheduleValues.Name;
              delete scheduleValues['Save Comments'];
              Object.keys(scheduleValues).forEach(scheduleValuesKey => {
                const newComment = scheduleValues[scheduleValuesKey].comments
                  .replace(props.initialValues[scheduleValuesKey].comments, '');
                if (newComment.length !== 0 && /\S/.test(newComment)) {
                  dispatch(createOrderNote({
                    values: {
                      'Order ID': scheduleValues[scheduleValuesKey].orderID,
                      'Author ID': account.ID,
                      Text: newComment,
                      ...noteCategories.length > 0
                        ? { 'Notes Category ID': noteCategories.map(item => item.ID) }
                        : {},
                    },
                  }));
                }
              });
            }
            return scheduleToPDF({
              schedule,
              values,
            }).then(PDF => ({
              blob: PDF.output('blob'),
              Name: values.Name,
            }));
          }}
          onSubmitSuccess={(values) => {
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
              window.navigator.msSaveOrOpenBlob(values.blob, `${values.Name}.pdf`);
            } else {
              printPdf(values.blob);
            }
          }}
          onCancelButtonClick={() => { history.push(backHref); }}
        />
      </Layout>
    );
  }
}

DeliverySchedulePrint.propTypes = {
  variant: PropTypes.oneOf(['week', 'day', 'month']).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
        ID: PropTypes.string.isRequired,
      }))),
    }),
  }).isRequired,
  noteCategories: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  account: PropTypes.shape({
    ID: PropTypes.number,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      dateOfDelivery: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  patients: PropTypes.arrayOf(PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
  })).isRequired,
  loading: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchPatientGlobalNotes: PropTypes.func.isRequired,
    fetchPatients: PropTypes.func.isRequired,
    fetchNoteCategories: PropTypes.func.isRequired,
  }).isRequired,
  record: PropTypes.arrayOf(PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  })).isRequired,
};

const mapStateToProps = store => ({
  patients: store.patients.data,
  loading: store.patients.loading,
  record: store.routerParams.record,
  noteCategories: store.noteCategories.data,
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchPatientGlobalNotes,
    fetchPatients,
    fetchNoteCategories,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  DeliverySchedulePrint);



// WEBPACK FOOTER //
// ./src/pages/workflow/DeliverySchedulePrint.js